import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faUsers,
  faBox,
  faExpand,
  faCompress,
} from "@fortawesome/pro-light-svg-icons";
import Button from "../../../../buttons/Button";

const WeeklyView = ({
  date,
  items = [],
  onViewItem,
  onSelectDate,
  isFullscreen = false,
  onToggleFullscreen,
  tKey = "dashboard.bookings.calendar.",
}) => {
  const { t } = useTranslation();

  // Start week on Monday
  const startOfWeek = new Date(date);
  const dayOfWeek = startOfWeek.getDay();
  startOfWeek.setDate(
    startOfWeek.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1),
  );

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    return day;
  });

  const getItemsForDay = (day) => {
    const dateStr = day.toISOString().split("T")[0];
    return items
      .filter((item) => {
        const itemDate = new Date(item.bookingDate).toISOString().split("T")[0];
        return itemDate === dateStr;
      })
      .sort((a, b) => {
        // Canceled/declined items go last
        const aCanceled = isCanceled(a) ? 1 : 0;
        const bCanceled = isCanceled(b) ? 1 : 0;
        if (aCanceled !== bCanceled) return aCanceled - bCanceled;
        return new Date(a.bookingDate) - new Date(b.bookingDate);
      });
  };

  const isToday = (day) => {
    const today = new Date();
    return day.toDateString() === today.toDateString();
  };

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const isCanceled = (item) =>
    item.displayStatus === "canceled" || item.displayStatus === "declined";

  return (
    <div className="bookings-weekly">
      <div className="bookings-weekly__header bookings-view__header">
        <div>
          <h4 className="bookings-weekly__title">{t(`${tKey}weeklyView`)}</h4>
          <p className="bookings-weekly__subtitle">
            {startOfWeek.toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "short",
            })}{" "}
            -{" "}
            {endOfWeek.toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
            })}
          </p>
        </div>
        {onToggleFullscreen && (
          <Button variant="white" icon onClick={onToggleFullscreen}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </Button>
        )}
      </div>

      <div className="bookings-weekly__grid">
        {weekDays.map((day) => {
          const dayItems = getItemsForDay(day);
          const today = isToday(day);

          return (
            <div
              key={day.toISOString()}
              className={`bookings-weekly__day ${today ? "bookings-weekly__day--today" : ""}`}
            >
              <div
                className="bookings-weekly__day-header"
                onClick={() => onSelectDate && onSelectDate(day)}
                style={{ cursor: onSelectDate ? "pointer" : "default" }}
              >
                <div className="bookings-weekly__day-name">
                  {day.toLocaleDateString("fr-FR", { weekday: "short" })}
                </div>
                <div className="bookings-weekly__day-number">
                  {day.getDate()}
                </div>
                <div className="bookings-weekly__day-count">
                  {dayItems.filter((i) => !isCanceled(i)).length} {t(`${tKey}bookingsShort`)}
                </div>
              </div>

              <div className="bookings-weekly__day-bookings">
                {dayItems.map((item) => {
                  const isOrder = item.type === "order";
                  const canceled = isCanceled(item);
                  const bDate = new Date(item.bookingDate);

                  let cardClass = "booking-card";
                  if (canceled) {
                    cardClass += " booking-card--canceled";
                  } else if (item.colorTheme) {
                    cardClass += ` booking-card--${item.colorTheme}`;
                  }

                  return (
                    <div
                      key={item.itemId}
                      className={cardClass}
                      onClick={() => onViewItem(item)}
                    >
                      <div className="booking-card__name">{item.guestName}</div>
                      <div className="booking-card__details-row">
                        <span className="booking-card__detail">
                          <FontAwesomeIcon icon={faClock} className="fa-icon" />
                          {bDate.toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <span className="booking-card__detail">
                          <FontAwesomeIcon icon={faUsers} className="fa-icon" />
                          {item.partySize}
                        </span>
                        {isOrder && (
                          <FontAwesomeIcon
                            icon={faBox}
                            className="booking-card__order-icon"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyView;
